<script setup lang="ts">
import PageHeader from "../PageHeader/PageHeader.vue";
import { defineEmits, defineProps } from "vue";
import { ref } from "vue";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isTodayNavigationActive: boolean;
  todayDatePicker: boolean;
  isWeekNavigationActive: boolean;
  weekDatePicker: boolean;
  selectedDate: Date;
  selectedStartDate: Date;
  selectedEndDate: Date;
  activeFilter: string;
  searchQuery: string;
  isLoading: boolean;
  activeViewList: any;
  activeStatusList: any;
  activeNamesList: any;
  isFilterOpen: any;
};

const props = defineProps<Props>();

type Emits = {
  (event: "goToPreviousDay"): void;
  (event: "goToNextDay"): void;
  (event: "resetToToday"): void;
  (event: "goToPreviousWeek"): void;
  (event: "goToNextWeek"): void;
  (event: "resetToThisWeek"): void;
  (event: "getAllProjects"): void;
  (event: "toggleFilterMenu"): void;
  (event: "setActiveFilter", filter: string): void;
  (event: "onChangeSearch", searchQuery: string): void;
};
const emits = defineEmits<Emits>();

const { t } = useTranslate();
const isSearchFocused = ref(false);
</script>

<template>
  <PageHeader>
    <template v-slot:header-left>
      <div>
        <!-- Statuses -->
        <div class="flex">
          <!-- Status -->

          <div class="flex gap-3">
            <!-- Day Filter -->
            <div class="flex items-center">
              <button
                v-if="!isTodayNavigationActive"
                @click="$emit('setActiveFilter', 'today')"
                id="today"
                class="flex gap-1 items-center p-2 cursor-pointer bg-transparent rounded-md hover:bg-[#d3d1cd] duration-300"
              >
                <img src="@/assets/Sun.svg" alt="Sun icon" />
                <p
                  class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
                >
                  {{ t("home.today") }}
                </p>
              </button>

              <div v-if="isTodayNavigationActive">
                <div
                  v-if="!todayDatePicker"
                  class="flex items-center gap-[6px] py-[6px] px-2 rounded-[7px] bg-white"
                >
                  <span
                    @click.stop="$emit('goToPreviousDay')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-left.svg" alt="Return arrow" />
                  </span>
                  <button @click="$emit('setActiveFilter', 'today')">
                    <p
                      class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
                    >
                      {{ t("home.today") }}
                    </p>
                  </button>
                  <span
                    @click.stop="$emit('goToNextDay')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-right.svg" alt="Return arrow" />
                  </span>
                </div>

                <div
                  v-if="todayDatePicker"
                  class="text-sm flex items-center gap-[6px] py-[6px] px-2 rounded-[7px] bg-white"
                >
                  <!-- Reset Button -->
                  <button
                    @click="$emit('resetToToday')"
                    class="w-3 h-3 rounded"
                  >
                    <img src="@/assets/return-arrow.svg" alt="Return arrow" />
                  </button>

                  <!-- Center Text -->
                  <button
                    @click="$emit('setActiveFilter', 'today')"
                    id="today"
                    class="flex cursor-pointer font-normal text-sm max-[910px]:text-[10px]"
                  >
                    <span>
                      {{
                        selectedDate.toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                        })
                      }}
                    </span>
                  </button>

                  <!-- Left Arrow -->
                  <span
                    @click.stop="$emit('goToPreviousDay')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-left.svg" alt="Return arrow" />
                  </span>

                  <!-- Right Arrow -->
                  <span
                    @click.stop="$emit('goToNextDay')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-right.svg" alt="Return arrow" />
                  </span>
                </div>
              </div>
            </div>
            <!-- Week Filter -->
            <div class="flex items-center">
              <button
                v-if="!isWeekNavigationActive"
                @click="$emit('setActiveFilter', 'week')"
                id="week"
                class="flex gap-1 items-center p-2 cursor-pointer bg-transparent rounded-md hover:bg-[#d3d1cd] duration-300"
              >
                <img src="@/assets/Calendar_Week.svg" alt="Sun icon" />
                <p
                  class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
                >
                  {{ t("home.this_week") }}
                </p>
              </button>

              <div v-if="isWeekNavigationActive">
                <div
                  v-if="!weekDatePicker"
                  class="flex items-center gap-[6px] py-[6px] px-2 rounded-[7px] bg-white"
                >
                  <span
                    @click.stop="$emit('goToPreviousWeek')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-left.svg" alt="Return arrow" />
                  </span>
                  <button @click="$emit('setActiveFilter', 'week')">
                    <p
                      class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
                    >
                      {{ t("home.this_week") }}
                    </p>
                  </button>
                  <span
                    @click.stop="$emit('goToNextWeek')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-right.svg" alt="Return arrow" />
                  </span>
                </div>

                <div
                  v-if="weekDatePicker"
                  class="text-sm flex items-center gap-[6px] py-[6px] px-2 rounded-[7px] bg-white"
                >
                  <!-- Reset Button -->
                  <button
                    @click="$emit('resetToThisWeek')"
                    class="w-3 h-3 rounded"
                  >
                    <img src="@/assets/return-arrow.svg" alt="Return arrow" />
                  </button>

                  <!-- Center Text -->
                  <button
                    @click="$emit('setActiveFilter', 'week')"
                    id="week"
                    class="flex cursor-pointer font-normal text-sm max-[910px]:text-[10px]"
                  >
                    <span>
                      {{
                        selectedStartDate.toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                        })
                      }}
                      -
                      {{
                        selectedEndDate.toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                        })
                      }}
                    </span>
                  </button>

                  <!-- Left Arrow -->
                  <span
                    @click.stop="$emit('goToPreviousWeek')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-left.svg" alt="Return arrow" />
                  </span>

                  <!-- Right Arrow -->
                  <span
                    @click.stop="$emit('goToNextWeek')"
                    class="cursor-pointer w-4 h-4 flex items-center justify-center"
                  >
                    <img src="@/assets/arrow-right.svg" alt="Return arrow" />
                  </span>
                </div>
              </div>
            </div>
            <!-- Abnahme Filter -->
            <button
              @click="$emit('setActiveFilter', 'abnahme')"
              id="abnahme"
              class="flex gap-1 items-center p-2 cursor-pointer rounded-md hover:bg-[#d3d1cd] duration-300"
              :class="
                activeFilter === 'abnahme' ? 'bg-white' : 'bg-transparent'
              "
            >
              <img src="@/assets/File_Check.svg" alt="Sun icon" />
              <p
                class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
              >
                {{ t("home.abnahme_ready") }}
              </p>
            </button>
            <!-- QC Filter -->
            <button
              @click="$emit('setActiveFilter', 'qc')"
              id="qc"
              class="flex gap-1 items-center p-2 cursor-pointer rounded-md hover:bg-[#d3d1cd] duration-300"
              :class="activeFilter === 'qc' ? 'bg-white' : 'bg-transparent'"
            >
              <img src="@/assets/Clipboard_Check.svg" alt="Sun icon" />
              <p
                class="font-normal text-primary-text-color text-sm max-[910px]:text-[14px]"
              >
                {{ t("home.qc_ready") }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:header-right>
      <!-- Search - filter -->
      <div class="flex items-center gap-3">
        <!-- Search -->
        <form
          class="max-w-[160px] max-[940px]:max-w-[84px] h-[30px] rounded-primary-radius px-[10px] border border-[#A7A5AE] bg-transparent flex items-center gap-1"
          :class="`${isSearchFocused ? 'bg-white ' : ''}`"
        >
          <div>
            <img
              class="w-4 h-4"
              src="@/assets/search-redesign.svg"
              alt="Search"
            />
          </div>
          <input
            class="w-full bg-transparent outline-none text-sm font-normal text-[#898790] placeholder:text-[#898790]"
            type="text"
            :placeholder="
              isSearchFocused
                ? t('home.project_name').value
                : t('home.search').value
            "
            :value="searchQuery"
            @input="(event:any)=>$emit('onChangeSearch',event.target.value)"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
          />
        </form>
        <!-- Filter -->
        <div class="flex items-center gap-2">
          <button
            @click="$emit('toggleFilterMenu')"
            class="relative flex items-center justify-center cursor-pointer hover:bg-white rounded-[7px] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)]"
            :class="isFilterOpen === true ? 'bg-white' : 'bg-transparent'"
            style="width: 40px; height: 40px"
          >
            <div
              class="w-[24px] h-[24px] rounded-[7px] flex items-center justify-center"
            >
              <img
                src="@/assets/filter-redesign.svg"
                alt="Filter"
                class="w-5 h-5"
              />
            </div>
            <!-- Badge -->
            <span
              v-if="
                activeViewList.length +
                  activeStatusList.length +
                  activeNamesList.length >
                0
              "
              class="absolute top-1 right-1 bg-[#393840] text-white text-[10px] font-bold rounded-[36px] w-[16px] h-[16px] px-[4px] py-[1px] flex items-center justify-center"
            >
              {{
                activeViewList.length +
                activeStatusList.length +
                activeNamesList.length
              }}
            </span>
          </button>
          <button
            @click="$emit('getAllProjects')"
            class="flex items-center justify-center cursor-pointer rounded-[7px] hover:bg-white hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] p-2"
            :class="isLoading ? 'bg-white ' : ''"
          >
            <img
              :class="isLoading ? 'animate-spin' : 'animate-none'"
              src="@/assets/refresh-redesign.svg"
              alt="Refresh"
            />
          </button>
        </div>
      </div>
    </template>
  </PageHeader>
</template>
